import { createLogic } from 'redux-logic';
import { normalize, schema } from 'normalizr';
import { camelizeKeys } from 'humps';
import { countBy, each, filter, find, flatMap, mapValues, size } from 'lodash';

// Action Types
const FETCH = '@@app/shopping-list/FETCH';
const FETCH_SUCCESS = '@@app/shopping-list/FETCH_SUCCESS';
const FETCH_ERROR = '@@app/shopping-list/FETCH_ERROR';

const TOGGLE = '@@app/shopping-list/TOGGLE';
const TOGGLE_SUCCESS = '@@app/shopping-list/TOGGLE_SUCCESS';
const TOGGLE_ERROR = '@@app/shopping-list/TOGGLE_ERROR';

const UNCHECK_ALL = '@@app/shopping-list/UNCHECK_ALL';
const UNCHECK_SUCCESS = '@@app/shopping-list/UNCHECK_SUCCESS';
const UNCHECK_ERROR = '@@app/shopping-list/UNCHECK_ERROR';

// Initial State
export const initialState = {
  isLoading: false,
  entities: {},
  checked: [],
};

// Observables
const fetchLogic = createLogic({
  type: FETCH,
  name: 'shoppingListFetchLogic',
  processOptions: {
    successType: FETCH_SUCCESS,
    failType: FETCH_ERROR,
  },
  process({ action, httpClient }) {
    const { level, memberId, recipeIds, sort } = action.payload;
    const endpoint = `/v2/shopping-lists/by-${sort}`;
    const params = { level, memberId, recipeIds };

    return httpClient
      .get(endpoint, { skipCamelize: true, params })
      .then(({ data }) => {
        return mapValues(data, ingredients => {
          return camelizeKeys(ingredients);
        });
      });
  },
});

const fetchSuccessLogic = createLogic({
  type: FETCH_SUCCESS,
  name: 'shoppingListFetchSuccessLogic',
  transform({ action }, next) {
    const payload = action.payload;
    const checked = {};

    each(filter(flatMap(payload), { checked: true }), item => {
      checked[item.foodId] = true;
    });

    next({ ...action, payload: { entities: payload, checked: checked } });
  },
});

const toggleLogic = createLogic({
  type: TOGGLE,
  name: 'shoppingListToggleLogic',
  processOptions: {
    successType: TOGGLE_SUCCESS,
    failType: TOGGLE_ERROR,
  },
  process({ action, httpClient }) {
    const { food, memberId } = action.payload;
    const { foodId, checked } = food;
    const type = checked ? 'uncheck' : 'check';
    const endpoint = `/v2/shopping-lists/${type}-ingredient?member_id=${memberId}&food_id=${foodId}`;

    return httpClient.put(endpoint).then(() => food);
  },
});

const uncheckAllLogic = createLogic({
  type: UNCHECK_ALL,
  name: 'shoppingListUncheckAllLogic',
  processOptions: {
    successType: UNCHECK_SUCCESS,
    failType: UNCHECK_ERROR,
  },
  process({ action, getState, httpClient }) {
    const { memberId } = action.payload;
    const endpoint = `/v2/shopping-lists/uncheck-all-ingredients?member_id=${memberId}`;

    return httpClient.put(endpoint).then(() => action.payload);
  },
});

export const shoppingListLogic = [
  fetchLogic,
  fetchSuccessLogic,
  toggleLogic,
  uncheckAllLogic,
];

// Action Creators
export const shoppingListFetch = (
  recipeIds,
  memberId,
  sort = 'category',
  level
) => {
  return { type: FETCH, payload: { recipeIds, memberId, sort, level } };
};

export const shoppingListToggleCheck = (food, memberId) => {
  return { type: TOGGLE, payload: { food, memberId } };
};

export const shoppingListUncheckAll = memberId => {
  return { type: UNCHECK_ALL, payload: { memberId } };
};

// Selectors
export const selectShoppingList = state => {
  return state.entities;
};

export const selectShoppingListChecked = state => {
  return state.checked;
};

export const selectShoppingListHasChecked = state => {
  return size(selectShoppingListChecked(state)) > 0;
};

export const selectShoppingListIsLoading = state => {
  return state.isLoading;
};

export const selectShoppingListMeta = (state, recipes) => {
  return mapValues(state.entities, (value, name) => {
    return {
      serves: (find(recipes, { name }) || {}).serves || 0,
      count: countBy(recipes, { name }).true,
    };
  });
};

// Reducer
export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH:
      return {
        ...state,
        isLoading: true,
      };

    case FETCH_SUCCESS:
      return {
        ...state,
        ...payload,
        isLoading: false,
      };

    case TOGGLE:
    case UNCHECK_ALL:
      return {
        ...state,
        isChecking: true,
      };

    case TOGGLE_SUCCESS:
      const checked = { ...state.checked };

      if (checked[payload.foodId]) {
        delete checked[payload.foodId];
      } else {
        checked[payload.foodId] = true;
      }

      return {
        ...state,
        checked,
        isChecking: false,
      };

    case UNCHECK_SUCCESS:
      return {
        ...state,
        checked: {},
        isChecking: false,
      };

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/logic/shoppingList.js